import React, { useState } from "react";

import BuildIcon from "@mui/icons-material/Build";
import CodeIcon from "@mui/icons-material/Code";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import {
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";

interface EnvironmentOption {
  slug: string;
  name: string;
}

interface StackActionMenuProps {
  // Context flags
  canPause: boolean;
  canResume: boolean;
  canDeploy: boolean;
  hasVcsRepo: boolean;
  vcsRepoUrl: string | null | undefined;
  actionLoading: boolean;
  needsVcsProvisioning: boolean;

  // Navigation
  onEdit: () => void;
  onDelete: () => void;

  // Deploy actions (overflow only)
  onPause: () => void;
  onResume: () => void;
  onProvisionVcs: () => void;
  onDeploy: () => void;

  // Promotion (deploy to a different environment)
  /** Environments available to promote to (excludes the stack's current env). */
  promotionTargets?: EnvironmentOption[];
  /** Called when the user selects a target environment to promote to. */
  onPromote?: (targetSlug: string) => void;
  /** True while a promote is in flight. */
  promoteLoading?: boolean;
}

export function StackActionMenu({
  canPause,
  canResume,
  canDeploy,
  hasVcsRepo,
  vcsRepoUrl,
  actionLoading,
  needsVcsProvisioning,
  onEdit,
  onDelete,
  onPause,
  onResume,
  onProvisionVcs,
  onDeploy,
  promotionTargets,
  onPromote,
  promoteLoading,
}: StackActionMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [promoteAnchorEl, setPromoteAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const open = Boolean(anchorEl);
  const promoteOpen = Boolean(promoteAnchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => {
    setAnchorEl(null);
    setPromoteAnchorEl(null);
  };

  const handleAction = (fn: () => void) => {
    handleClose();
    fn();
  };

  const handlePromoteOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setPromoteAnchorEl(e.currentTarget);
  };
  const handlePromoteClose = () => {
    setPromoteAnchorEl(null);
  };

  return (
    <>
      <Tooltip title="More actions">
        <IconButton
          size="small"
          onClick={handleOpen}
          disabled={actionLoading}
          aria-label="More stack actions"
          data-testid="stack-actions-menu"
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        {/* Pause / Resume toggle */}
        {canPause && (
          <MenuItem
            onClick={() => handleAction(onPause)}
            disabled={actionLoading}
          >
            <ListItemIcon>
              <PauseIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Pause</ListItemText>
          </MenuItem>
        )}
        {canResume && (
          <MenuItem
            onClick={() => handleAction(onResume)}
            disabled={actionLoading}
          >
            <ListItemIcon>
              <PlayArrowIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Resume</ListItemText>
          </MenuItem>
        )}

        {/* Provision VCS (when needed for draft stacks) */}
        {needsVcsProvisioning && (
          <MenuItem
            onClick={() => handleAction(onProvisionVcs)}
            disabled={actionLoading}
          >
            <ListItemIcon>
              <BuildIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Provision VCS</ListItemText>
          </MenuItem>
        )}

        {/* Deploy-only (redeploy without rebuild) — available when primary is "Build & Deploy" */}
        {canDeploy && (
          <MenuItem
            onClick={() => handleAction(onDeploy)}
            disabled={actionLoading}
          >
            <ListItemIcon>
              <RocketLaunchIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Deploy</ListItemText>
          </MenuItem>
        )}

        <Divider />

        {/* Promote to environment */}
        {promotionTargets && promotionTargets.length > 0 && onPromote && (
          <>
            <MenuItem
              disabled={actionLoading || promoteLoading}
              onClick={handlePromoteOpen}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <ListItemIcon>
                {promoteLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <RocketLaunchIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={600}>
                    Promote to
                  </Typography>
                }
              />
            </MenuItem>
            <Menu
              anchorEl={promoteAnchorEl}
              open={promoteOpen}
              onClose={handlePromoteClose}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              slotProps={{ paper: { sx: { minWidth: 180 } } }}
            >
              {promotionTargets.map((env) => (
                <MenuItem
                  key={env.slug}
                  disabled={promoteLoading}
                  onClick={() => {
                    handleClose();
                    onPromote(env.slug);
                  }}
                  sx={{ borderRadius: 1, py: 0.5 }}
                >
                  <ListItemText
                    primary={env.name}
                    secondary={env.slug}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                </MenuItem>
              ))}
            </Menu>
          </>
        )}

        <Divider />

        {/* Edit */}
        <MenuItem onClick={() => handleAction(onEdit)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit configuration</ListItemText>
        </MenuItem>

        {/* VS Code */}
        {hasVcsRepo && vcsRepoUrl && (
          <MenuItem
            component="a"
            href={`vscode://vscode.git/clone?url=${encodeURIComponent(vcsRepoUrl)}`}
            onClick={handleClose}
          >
            <ListItemIcon>
              <CodeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Open in VS Code</ListItemText>
          </MenuItem>
        )}

        <Divider />

        {/* Delete */}
        <MenuItem
          onClick={() => handleAction(onDelete)}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteForeverIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete stack</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

export default StackActionMenu;
